import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, ShieldCheck, Lock, ArrowLeft, Star } from "lucide-react";
import { toast } from "sonner";
import { Stars } from "@/components/travel/Stars";
export default function Messages() {
  const { contactId } = useParams();
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showEscrow, setShowEscrow] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [escrow, setEscrow] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    (async () => {
      const r = await api.get("/contacts/mine");
      setContacts(r.data);
      if (contactId) {
        const c = r.data.find(x => x.contact_id === contactId);
        if (c) setActive(c);
      } else if (r.data[0]) setActive(r.data[0]);
    })();
  }, [contactId]);

  useEffect(() => {
    if (!active) return;
    const load = async () => {
      try {
        const [m, e] = await Promise.all([
          api.get(`/messages/${active.contact_id}`),
          api.get(`/escrow/by-contact/${active.contact_id}`),
        ]);
        setMessages(m.data);
        setEscrow(e.data);
      } catch (err) { console.error("messages poll failed", err); }
    };
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { contact_id: active.contact_id, body: text });
      setText("");
      const m = await api.get(`/messages/${active.contact_id}`);
      setMessages(m.data);
    } catch { toast.error("Erreur envoi"); }
  };

  if (!user) return <div className="p-8 text-center text-slate-400">Connectez-vous.</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 grid md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-140px)]">
      <aside className="glass rounded-xl p-2 overflow-auto">
        <div className="text-xs text-slate-400 px-2 py-1">Conversations</div>
        {contacts.map(c => (
          <Link data-testid={`msg-thread-${c.contact_id}`} to={`/messages/${c.contact_id}`} key={c.contact_id}
            className={`block px-3 py-2 rounded-lg text-sm ${active?.contact_id === c.contact_id ? "bg-slate-800" : "hover:bg-slate-800/50"}`}>
            <div className="font-semibold truncate">{c.counterpart?.name}</div>
            <div className="text-xs text-slate-400 truncate">{c.trip?.origin?.label} → {c.trip?.destination?.label}</div>
          </Link>
        ))}
        {contacts.length === 0 && <p className="text-xs text-slate-500 p-3">Aucune conversation. Débloquez un trajet.</p>}
      </aside>

      <section className="glass rounded-xl flex flex-col overflow-hidden">
        {active ? (
          <>
            <header className="border-b border-slate-800 p-3 flex items-center gap-3">
              <Link to="/dashboard" className="md:hidden text-slate-400"><ArrowLeft size={16}/></Link>
              <Link to={`/profile/${active.counterpart?.user_id}`} data-testid="msg-open-profile" className="flex-1">
                <div className="text-sm font-semibold">{active.counterpart?.name}</div>
                <div className="flex items-center gap-1 text-[11px] text-slate-400"><Stars value={active.counterpart?.rating_avg || 0} size={10}/> {active.trip?.origin?.label} → {active.trip?.destination?.label}</div>
              </Link>
              <button data-testid="msg-open-escrow" onClick={()=>setShowEscrow(true)} className="btn-coral px-3 py-1.5 rounded-lg text-xs flex items-center gap-1">
                <ShieldCheck size={12}/> Finaliser la mission
              </button>
            </header>

            <div className="p-3 border-b border-slate-800 bg-slate-900/40 text-xs flex items-center gap-2">
              <Lock size={12} className="text-[#F59E0B]"/> Sécurise cette mission avec le séquestre. Numéros/emails masqués automatiquement.
              {escrow && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-300">Séquestre : <b>{escrow.status}</b> · {escrow.amount}€</span>}
            </div>

            <div ref={boxRef} className="flex-1 overflow-auto p-4 space-y-2">
              {messages.map(m => {
                const mine = m.sender_id === user.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-gradient-to-br from-[#FF6B6B] to-[#FA5252] text-white" : "glass"}`}>
                      {m.body}
                      {m.was_masked && <div className="text-[10px] mt-1 opacity-70">Contenu masqué par TravelBuddy</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={send} className="border-t border-slate-800 p-3 flex gap-2">
              <input data-testid="msg-input" value={text} onChange={(e)=>setText(e.target.value)} placeholder="Écrivez un message…"
                className="flex-1 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 outline-none focus:border-[#FF6B6B] text-sm"/>
              <button data-testid="msg-send" className="btn-coral px-4 py-2 rounded-lg text-sm flex items-center gap-1"><Send size={14}/> Envoyer</button>
            </form>

            {active && (
              <div className="border-t border-slate-800 px-3 py-2 flex justify-end">
                <button data-testid="msg-open-review" onClick={()=>setShowReview(true)} className="btn-ghost-glass px-3 py-1.5 rounded-lg text-xs flex items-center gap-1">
                  <Star size={12}/> Laisser un avis
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Sélectionnez une conversation</div>
        )}
      </section>

      {showEscrow && active && <EscrowModal contact={active} escrow={escrow} onClose={()=>setShowEscrow(false)} onRefresh={async ()=>{ const e = await api.get(`/escrow/by-contact/${active.contact_id}`); setEscrow(e.data); }} />}
      {showReview && active && <ReviewModal target={active.counterpart} tripId={active.trip_id} onClose={()=>setShowReview(false)} />}
    </div>
  );
}

function EscrowModal({ contact, escrow, onClose, onRefresh }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState(escrow?.amount || 100);
  const [loading, setLoading] = useState(false);
  const create = async () => {
    setLoading(true);
    try {
      const r = await api.post("/escrow/create", { contact_id: contact.contact_id, amount: parseFloat(amount), origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setLoading(false); }
  };
  const adminFund = async () => {
    setLoading(true);
    try {
      // create escrow first if none
      let esc = escrow;
      if (!esc) {
        const r = await api.post("/escrow/create", { contact_id: contact.contact_id, amount: parseFloat(amount), origin_url: window.location.origin });
        esc = { escrow_id: r.data.escrow_id };
      }
      await api.post("/admin/simulate/fund-escrow", { escrow_id: esc.escrow_id });
      toast.success("Séquestre gelé (admin)");
      await onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const release = async () => {
    setLoading(true);
    try {
      await api.post(`/escrow/${escrow.escrow_id}/release`);
      toast.success("Fonds libérés au voyageur");
      await onRefresh();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-strong rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-2 text-[#F59E0B] mb-2"><ShieldCheck size={16}/> <span className="text-xs uppercase tracking-wider">Séquestre de mission</span></div>
        <h3 className="font-display text-lg font-bold mb-4">Sécuriser la mission</h3>
        {!escrow || escrow.status === "pending" ? (
          <>
            <label className="text-xs text-slate-400">Montant à séquestrer (EUR)</label>
            <input data-testid="escrow-amount" type="number" min="1" value={amount} onChange={(e)=>setAmount(e.target.value)}
              className="w-full mt-1 mb-4 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none focus:border-[#FF6B6B]"/>
            <p className="text-xs text-slate-400 mb-4">Vos fonds seront gelés chez Stripe. Ils ne seront libérés qu'après votre confirmation de réception.</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-ghost-glass px-4 py-2 rounded-lg text-sm flex-1">Annuler</button>
              <button data-testid="escrow-fund" disabled={loading} onClick={create} className="btn-coral px-4 py-2 rounded-lg text-sm flex-1">{loading ? "…" : "Payer & geler"}</button>
            </div>
            {user?.is_admin && (
              <button data-testid="escrow-admin-fund" disabled={loading} onClick={adminFund} className="mt-2 w-full btn-ghost-glass px-4 py-2 rounded-lg text-xs border border-[#FF6B6B]/40 text-[#FF6B6B]">Admin : geler sans Stripe (démo)</button>
            )}
          </>
        ) : escrow.status === "funded" ? (
          <>
            <p className="text-sm text-slate-300 mb-4">Fonds gelés : <b>{escrow.amount}€</b>. À la réception du colis, libérez-les.</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-ghost-glass px-4 py-2 rounded-lg text-sm flex-1">Plus tard</button>
              <button data-testid="escrow-release" disabled={loading} onClick={release} className="btn-coral px-4 py-2 rounded-lg text-sm flex-1">{loading ? "…" : "Confirmer réception"}</button>
            </div>
          </>
        ) : (
          <p className="text-sm text-emerald-400">Séquestre {escrow.status} · {escrow.amount}€</p>
        )}
      </div>
    </div>
  );
}

function ReviewModal({ target, tripId, onClose }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/reviews", { target_user_id: target.user_id, trip_id: tripId, rating, comment });
      toast.success("Merci pour votre avis");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-strong rounded-2xl w-full max-w-md p-6">
        <h3 className="font-display text-lg font-bold mb-1">Noter {target?.name}</h3>
        <p className="text-xs text-slate-400 mb-4">Votre avis aide la communauté.</p>
        <div className="flex gap-1 mb-4">
          {[1,2,3,4,5].map(n => (
            <button key={n} data-testid={`review-star-${n}`} onClick={()=>setRating(n)}>
              <Star size={28} className={n<=rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-slate-600"}/>
            </button>
          ))}
        </div>
        <textarea data-testid="review-comment" rows={3} value={comment} onChange={(e)=>setComment(e.target.value)} placeholder="Un mot ?"
          className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm outline-none focus:border-[#FF6B6B] mb-4"/>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost-glass px-4 py-2 rounded-lg text-sm flex-1">Annuler</button>
          <button data-testid="review-submit" disabled={loading} onClick={submit} className="btn-coral px-4 py-2 rounded-lg text-sm flex-1">{loading ? "…" : "Envoyer"}</button>
        </div>
      </div>
    </div>
  );
}
